import { ChevronDown, ChevronRight } from 'lucide-react'
import { useContext, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import CreateEnrollmentButton from 'components/CreateEnrollmentButton'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Banner, Button } from 'slab'
import { formatMoney } from 'utils'
import { useGetBrokersMembersMemberIdEnrollmentsCostOfCoverage } from 'utils/web_api'
import { type BrokersMoney } from 'utils/web_api.schemas'

import MemberCostOfCoverage from './MemberCostOfCoverage'

interface BrokersMembersEnrollmentsProps {
  memberId: string
  isTieredAllowancesEmployer: boolean
  isUserThatchAdmin: boolean
}

const BrokersMembersEnrollmentsSummary = ({
  memberId,
  isTieredAllowancesEmployer,
  isUserThatchAdmin,
}: BrokersMembersEnrollmentsProps) => {
  const { planYear } = useContext(BrokersMemberContext)
  const [showDetails, setShowDetails] = useState(false)
  const currentMonth = new Date().getMonth() + 1

  const {
    data: currentMonthData,
    isLoading,
    error,
  } = useGetBrokersMembersMemberIdEnrollmentsCostOfCoverage(memberId, {
    plan_year: planYear || 0,
    start_month: currentMonth,
    end_month: currentMonth,
  }, {
    query: {
      // Enable when we have valid data
      enabled: !!memberId && !!planYear,
      refetchOnWindowFocus: false,
    },
  })

  const handleViewDetails = () => {
    setShowDetails(!showDetails)
  }

  if (isLoading) {
    return (
      <div className='flex justify-between items-center mb-2'>
        <SkeletonItem widthClass='w-80' heightClass='h-10' />
        {planYear && (!isTieredAllowancesEmployer || isUserThatchAdmin) && (
          <div className='flex w-40'>
            <CreateEnrollmentButton memberId={memberId} planYear={planYear} isTieredAllowancesEmployer={isTieredAllowancesEmployer} />
          </div>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex justify-between items-center mb-2'>
        <Banner type='critical' title='Error loading cost of coverage' />
        {(!isTieredAllowancesEmployer || isUserThatchAdmin) && planYear && (
          <div className='flex w-40'>
            <CreateEnrollmentButton memberId={memberId} planYear={planYear} isTieredAllowancesEmployer={isTieredAllowancesEmployer} />
          </div>
        )}
      </div>
    )
  }

  const currentMonthCost =
    currentMonthData?.monthly_costs?.[currentMonth.toString()]

  return (
    <div className='mb-4'>
      {/* Current month display with create enrollment button */}
      <div className='flex justify-between items-center'>
        <div className='flex items-center justify-between gap-2'>
          <div className='heading-md'>
            <span className='text-gray-600'>Plan enrollments </span>
            <span className='font-semibold text-gray-600'>
              {formatMoney(parseFloat((currentMonthCost as BrokersMoney)?.value || '0'))} /mo
            </span>
          </div>

          <Button
            onClick={handleViewDetails}
            variant='tertiary'
            rightIcon={showDetails ? ChevronDown : ChevronRight}
          >
            {showDetails ? 'Hide details' : 'View details'}
          </Button>
        </div>
        {/*
         * We allow thatch admins (and thatch admins only) to create enrollments for tiered allowances employers.
         * When this is done, it's required that the max_premium_reimbursement_amount is set, and
         * we entrust our ops team to set the correct value.  External brokers have not set this appropriately in
         * the past.
         */}
        {(!isTieredAllowancesEmployer || isUserThatchAdmin) && planYear && (
          <div className='flex w-40'>
            <CreateEnrollmentButton memberId={memberId} planYear={planYear} isTieredAllowancesEmployer={isTieredAllowancesEmployer} />
          </div>
        )}
      </div>

      <AnimateHeightAndOpacity isOpen={showDetails}>
        <div className='py-4'>
          <MemberCostOfCoverage
            memberId={memberId}
            isDisplayed={showDetails}
          />
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}

export default BrokersMembersEnrollmentsSummary
